import { captureException } from "@sentry/react";
import type { ScopeContext } from "@sentry/core";
import type { SWRConfiguration } from "swr";

import { CONFIGURATION_PRODUCT_QUERY } from "constants/configuration";
import { isProductionEnv } from "helpers/env-checks";
import { captureResponseError, throwResponseErrorAsync } from "helpers/errors";
import { getSwrResourceFromKey } from "helpers/swr";

const KEY = import.meta.env.PLATFORM_AUTH_KEY as string;

// refetching on dev(ish) environments to prevent a reload; shouldn't be needed in prod
export const generateConfigurationSwrConfig = (hint: Partial<ScopeContext>) =>
  ({
    revalidateIfStale: !isProductionEnv(),
    revalidateOnFocus: !isProductionEnv(),
    revalidateOnReconnect: !isProductionEnv(),
    onError: (error, key) => {
      const resource = getSwrResourceFromKey(key, captureException);

      captureResponseError(error as Error, {
        tags: {
          ...hint?.tags,
          resource,
        },
      });
    },
  }) satisfies SWRConfiguration;

export const configurationFetcher = async <T>(
  requestKey: string | ReadonlyArray<string>,
  token: string,
): Promise<T> => {
  const response = await fetch(
    `${resolveConfigurationUrl(requestKey)}?${CONFIGURATION_PRODUCT_QUERY}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Ocp-Apim-Subscription-Key": KEY,
      },
    },
  );

  const resource = (
    Array.isArray(requestKey) ? requestKey[1] : requestKey
  ) as string;

  if (!response.ok) {
    await throwResponseErrorAsync(
      `Failed to fetch configuration for ${resource}`,
      response,
    );
  }

  return response.json() as Promise<T>;
};
export const resolveConfigurationUrl = (
  requestKey: string | ReadonlyArray<string>,
): string => {
  if (!Array.isArray(requestKey)) {
    return requestKey as string;
  }

  const [url, ...resources] = requestKey as ReadonlyArray<string>;
  const resourcePath = resources.filter(Boolean).join("/");

  return resourcePath ? `${url}/${resourcePath}` : url;
};
